import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject, input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormField } from '@bodyanalytics/data-models-ui';

import { FormSubmitButtonComponent } from './form-submit-button.component';
import { FieldInputComponent } from '../forms/field-input/field-input.component';
import { FieldLabelComponent } from '../forms/field-label/field-label.component';
import { FieldSelectComponent, SelectOption } from '../forms/field-select/field-select.component';
import { FieldToggleComponent } from '../forms/field-toggle/field-toggle.component';

/** Kinds of validation failure the template maps to a message. */
export type GenericFormErrorKind = 'required' | 'email' | 'invalid';

let idCounter = 0;

/**
 * Schema-driven form. Each `FormField` renders through the canonical ui-core field components
 * (`field-input`, `field-select`, `field-toggle`) — label above, hint/error below (R-FORM-03/05).
 * Errors only show after the first submit attempt.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-generic-form',
  templateUrl: './generic-form.component.html',
  styleUrls: ['./generic-form.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatTooltipModule,
    FieldInputComponent,
    FieldSelectComponent,
    FieldToggleComponent,
    FieldLabelComponent,
    FormSubmitButtonComponent,
  ],
})
export class GenericFormComponent<T> implements OnInit {
  private formBuilder = inject(FormBuilder);
  private readonly idPrefix = `bx-generic-form-${++idCounter}`;

  genericFormGroup!: FormGroup;
  @Input()
  fields: FormField[] = [];
  readonly submitLabel = input<string | undefined>(undefined);
  @Output() formSubmitted = new EventEmitter<T>();
  public isFormSubmitted = false;

  ngOnInit() {
    this.genericFormGroup = this.formBuilder.group({});
    this.fields.forEach(field => {
      const initial = field.type === 'checkbox' ? false : null;
      this.genericFormGroup.addControl(field.key, new FormControl(initial, field.validation || []));
    });
  }

  fieldId(field: FormField): string {
    return `${this.idPrefix}-${field.key}`;
  }

  /** Select/textarea/checkbox are rendered by their own @case; every other kind is a field-input type. */
  inputType(field: FormField): ReturnType<FieldInputComponent['type']> {
    return field.type as ReturnType<FieldInputComponent['type']>;
  }

  selectOptions(field: FormField): SelectOption<string>[] {
    return field.options ?? [];
  }

  errorKind(field: FormField): GenericFormErrorKind | null {
    const errors = this.genericFormGroup.get(field.key)?.errors;
    if (!this.isFormSubmitted || !errors) {
      return null;
    }
    if (errors['required']) {
      return 'required';
    }
    return errors['email'] ? 'email' : 'invalid';
  }

  /** Message for the field's current validation failure, shown only after a submit attempt. */
  errorMessage(field: FormField): string | undefined {
    switch (this.errorKind(field)) {
      case 'required':
        return `${field.label} is required.`;
      case 'email':
        return 'Enter a valid email address.';
      case 'invalid':
        return `Enter a valid value for ${field.label}.`;
      default:
        return undefined;
    }
  }

  onSubmit() {
    this.isFormSubmitted = true; // Track form submission for error display

    if (this.genericFormGroup.valid) {
      this.formSubmitted.emit(this.genericFormGroup.value);
      this.isFormSubmitted = false; // Reset for subsequent submissions
    }
  }
}
